import { useEffect, useState } from 'react';
import axios from 'axios';
import { Search, UserPlus, Mail, X } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { UserAvatar } from './ui/Avatar';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Reusable admin modal for linking or inviting a parent/guardian into a
 * player's household. Aug 2026 MVP — link + invite only.
 *
 * Props:
 *   householdId   : string   — required, the target household
 *   playerId      : string?  — optional, tracked in audit only
 *   playerName    : string?  — optional, display in modal header
 *   onClose       : ()  =>   — closes modal
 *   onSuccess     : ()  =>   — parent refetches after success
 */
export default function AdminLinkGuardianModal({
  householdId,
  playerId,
  playerName,
  onClose,
  onSuccess,
}) {
  const [tab, setTab] = useState('link');
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  if (!householdId) return null;

  const handleSuccess = (msg) => {
    setSuccessMsg(msg);
    setErrorMsg('');
    if (onSuccess) onSuccess();
    setTimeout(() => onClose(), 1200);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      data-testid="link-guardian-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between border-b p-4">
          <div>
            <h2 className="text-xl font-bold text-navy">Link Parent / Guardian</h2>
            {playerName && (
              <p className="text-xs text-gray-500 mt-0.5">for {playerName}</p>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-700"
            data-testid="link-guardian-close-btn"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex border-b bg-gray-50">
          <button
            onClick={() => { setTab('link'); setErrorMsg(''); }}
            className={`flex-1 py-3 text-sm font-semibold transition-colors ${
              tab === 'link'
                ? 'text-navy border-b-2 border-navy bg-white'
                : 'text-gray-500 hover:text-navy'
            }`}
            data-testid="link-guardian-tab-existing"
          >
            <Search className="w-4 h-4 inline mr-1.5 -mt-0.5" />
            Link Existing Parent
          </button>
          <button
            onClick={() => { setTab('invite'); setErrorMsg(''); }}
            className={`flex-1 py-3 text-sm font-semibold transition-colors ${
              tab === 'invite'
                ? 'text-navy border-b-2 border-navy bg-white'
                : 'text-gray-500 hover:text-navy'
            }`}
            data-testid="link-guardian-tab-invite"
          >
            <UserPlus className="w-4 h-4 inline mr-1.5 -mt-0.5" />
            Invite New Parent / Guardian
          </button>
        </div>

        {successMsg && (
          <div
            className="m-4 p-3 bg-green-50 border border-green-200 text-green-800 rounded text-sm"
            data-testid="link-guardian-success"
          >
            {successMsg}
          </div>
        )}
        {errorMsg && (
          <div
            className="m-4 p-3 bg-red-50 border border-red-200 text-red-800 rounded text-sm"
            data-testid="link-guardian-error"
          >
            {errorMsg}
          </div>
        )}

        {tab === 'link' && (
          <LinkExistingPanel
            householdId={householdId}
            playerId={playerId}
            submitting={submitting}
            setSubmitting={setSubmitting}
            setErrorMsg={setErrorMsg}
            onSuccess={(name) => handleSuccess(`${name} linked to household`)}
          />
        )}
        {tab === 'invite' && (
          <InviteNewPanel
            householdId={householdId}
            playerId={playerId}
            submitting={submitting}
            setSubmitting={setSubmitting}
            setErrorMsg={setErrorMsg}
            onSuccess={(email) => handleSuccess(`Invitation sent to ${email}`)}
          />
        )}
      </div>
    </div>
  );
}

function LinkExistingPanel({
  householdId, playerId, submitting, setSubmitting, setErrorMsg, onSuccess,
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [relationship, setRelationship] = useState('guardian');
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    if (!query || query.length < 2) {
      setResults([]);
      return;
    }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await axios.get(
          `${API_URL}/api/admin/users?search=${encodeURIComponent(query)}`,
          { withCredentials: true },
        );
        setResults((r.data?.users || []).slice(0, 15));
      } catch (e) {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [query]);

  const submit = async () => {
    if (!selected) return;
    setSubmitting(true);
    setErrorMsg('');
    try {
      await axios.post(
        `${API_URL}/api/admin/households/${householdId}/guardians/link`,
        { user_id: selected.user_id, relationship, player_id: playerId || undefined },
        { withCredentials: true },
      );
      onSuccess(selected.name || selected.email);
    } catch (e) {
      setErrorMsg(e.response?.data?.detail || 'Link failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-4 space-y-4">
      <div>
        <label className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-1 block">
          Search by name, email, or phone
        </label>
        <Input
          value={query}
          onChange={(e) => { setQuery(e.target.value); setSelected(null); }}
          placeholder="Type at least 2 characters..."
          data-testid="link-guardian-search-input"
        />
      </div>

      {searching && <p className="text-xs text-gray-500">Searching...</p>}

      {results.length > 0 && !selected && (
        <div className="border rounded max-h-64 overflow-y-auto divide-y">
          {results.map((u) => (
            <button
              key={u.user_id}
              onClick={() => setSelected(u)}
              className="w-full flex items-center gap-3 p-3 hover:bg-gray-50 text-left"
              data-testid={`link-guardian-result-${u.user_id}`}
            >
              <UserAvatar photoUrl={u.picture} name={u.name} size="sm" />
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm truncate">{u.name || '(no name)'}</div>
                <div className="text-xs text-gray-500 truncate">{u.email}</div>
                {u.household_id && (
                  <div className="text-xs text-orange-600 truncate">
                    {u.household_id === householdId
                      ? 'Already in this household'
                      : 'Belongs to another household'}
                  </div>
                )}
              </div>
            </button>
          ))}
        </div>
      )}

      {selected && (
        <div className="border-2 border-navy rounded p-3 flex items-center gap-3">
          <UserAvatar photoUrl={selected.picture} name={selected.name} size="sm" />
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-sm truncate">{selected.name}</div>
            <div className="text-xs text-gray-600 truncate">{selected.email}</div>
          </div>
          <button
            onClick={() => setSelected(null)}
            className="text-xs text-gray-500 hover:underline"
          >
            Change
          </button>
        </div>
      )}

      {selected && (
        <div>
          <label className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-1 block">
            Relationship
          </label>
          <Select value={relationship} onValueChange={setRelationship}>
            <SelectTrigger data-testid="link-guardian-relationship-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="mother">Mother</SelectItem>
              <SelectItem value="father">Father</SelectItem>
              <SelectItem value="guardian">Guardian</SelectItem>
              <SelectItem value="grandparent">Grandparent</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="flex justify-end pt-3 border-t">
        <Button
          onClick={submit}
          disabled={!selected || submitting}
          data-testid="link-guardian-link-submit-btn"
        >
          {submitting ? 'Linking...' : 'Link to Household'}
        </Button>
      </div>
    </div>
  );
}

function InviteNewPanel({
  householdId, playerId, submitting, setSubmitting, setErrorMsg, onSuccess,
}) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [relationship, setRelationship] = useState('guardian');

  const canSubmit = firstName.trim() && lastName.trim() && email.trim();

  const submit = async () => {
    setSubmitting(true);
    setErrorMsg('');
    try {
      await axios.post(
        `${API_URL}/api/admin/households/${householdId}/guardians/invite`,
        {
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          email: email.trim(),
          phone: phone.trim() || undefined,
          relationship,
          player_id: playerId || undefined,
        },
        { withCredentials: true },
      );
      onSuccess(email);
    } catch (e) {
      setErrorMsg(e.response?.data?.detail || 'Invitation failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-4 space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-1 block">
            First name
          </label>
          <Input
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            data-testid="invite-guardian-first-name-input"
          />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-1 block">
            Last name
          </label>
          <Input
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            data-testid="invite-guardian-last-name-input"
          />
        </div>
      </div>
      <div>
        <label className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-1 block">
          Email
        </label>
        <Input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="parent@example.com"
          data-testid="invite-guardian-email-input"
        />
      </div>
      <div>
        <label className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-1 block">
          Mobile phone (optional)
        </label>
        <Input
          type="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="+15555550100"
          data-testid="invite-guardian-phone-input"
        />
      </div>
      <div>
        <label className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-1 block">
          Relationship to player
        </label>
        <Select value={relationship} onValueChange={setRelationship}>
          <SelectTrigger data-testid="invite-guardian-relationship-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="mother">Mother</SelectItem>
            <SelectItem value="father">Father</SelectItem>
            <SelectItem value="guardian">Guardian</SelectItem>
            <SelectItem value="grandparent">Grandparent</SelectItem>
            <SelectItem value="other">Other</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="flex justify-end pt-3 border-t">
        <Button
          onClick={submit}
          disabled={!canSubmit || submitting}
          data-testid="invite-guardian-submit-btn"
        >
          <Mail className="w-4 h-4 mr-1.5" />
          {submitting ? 'Sending...' : 'Send Invitation'}
        </Button>
      </div>
    </div>
  );
}
